import { createPdfHelpers, uploadAndRegisterPdf } from './gestion-tecnica-documentos'
import { RulaAssessmentRow } from './gestion-tecnica-rula'
import { calcularPuntuacionGrupoA, calcularPuntuacionGrupoB } from './rula'

// Genera el reporte técnico PDF de una evaluación RULA, siguiendo el mismo
// patrón que generateNoiseReportPDF(): createPdfHelpers() para el flujo de
// texto + uploadAndRegisterPdf() para subir el blob y registrarlo en
// sso_documents.
//
// checklist_item_id: 'GT-06' — se investigó si existía un ítem real del
// checklist Anexo 1 MDT para evaluación de riesgo ergonómico antes de usar
// un código ad-hoc (mismo criterio que PO-08 para CD 513). Sí existe: GT-06
// en compliance-engine.ts, "Informe de evaluación de riesgos ergonómicos y
// psicosociales" (Decisión 584 Art. 11, DE 255 (2024) Art. 44/45/46, AM 196
// (2024)), modulo_storsafe: 'Documentos SSO' — no estaba conectado a ningún
// módulo de cálculo todavía (a diferencia de GT-15/16/17, reservados para
// Higiene Física/Química/Biológica manual). No se usó GT-20 porque este
// ítem real ya cubre exactamente el alcance del reporte RULA.
export async function generateRulaReportPDF(
  evaluacion: RulaAssessmentRow,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const helpers = createPdfHelpers()
  const { txt, skip, hline, doc, drawTable } = helpers

  const { inputs, resultado } = evaluacion

  // a. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Reporte de Evaluación Ergonómica — Método RULA', 12, 'bold', 'center', 0, 6)
  txt(`${evaluacion.descripcion} — Lado ${evaluacion.lado === 'izquierdo' ? 'Izquierdo' : 'Derecho'}`, 11, 'bold', 'center', 0, 6)
  hline()

  // b. Datos de entrada
  txt('Datos de entrada', 11, 'bold', 'left', 0, 4)
  txt('Fuente metodológica: RULA (Rapid Upper Limb Assessment — McAtamney & Corlett, 1993)', 9, 'italic', 'left', 0, 3)

  txt('Grupo A — Miembros superiores', 10, 'bold', 'left', 0, 3)
  txt(`Brazo: ${inputs.rangoBrazo} — hombro elevado/rotado: ${inputs.modBrazo.hombroElevadoORotado ? 'sí' : 'no'}, abducido: ${inputs.modBrazo.abducido ? 'sí' : 'no'}, punto de apoyo: ${inputs.modBrazo.puntoApoyo ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Antebrazo: ${inputs.rangoAntebrazo} — modificador: ${inputs.modAntebrazo}`, 9, 'normal', 'left', 4, 2)
  txt(`Muñeca: ${inputs.rangoMuneca} — modificador: ${inputs.modMuneca}`, 9, 'normal', 'left', 4, 2)
  txt(`Giro de muñeca: ${inputs.rangoGiroMuneca}`, 9, 'normal', 'left', 4, 4)

  txt('Grupo B — Cuello, tronco y piernas', 10, 'bold', 'left', 0, 3)
  txt(`Cuello: ${inputs.rangoCuello} — rotada: ${inputs.modCuello.rotada ? 'sí' : 'no'}, inclinación lateral: ${inputs.modCuello.inclinacionLateral ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Tronco: ${inputs.rangoTronco} — rotado: ${inputs.modTronco.rotado ? 'sí' : 'no'}, inclinación lateral: ${inputs.modTronco.inclinacionLateral ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Piernas: ${inputs.condicionPiernas}`, 9, 'normal', 'left', 4, 4)

  txt('Actividad y carga/fuerza', 10, 'bold', 'left', 0, 3)
  txt(`Actividad estática: ${inputs.tipoActividad.estatica ? 'sí' : 'no'}, repetitiva: ${inputs.tipoActividad.repetitiva ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Carga/fuerza: ${inputs.cargaFuerza}`, 9, 'normal', 'left', 4, 4)
  hline()

  // c. Resultado
  txt('Resultado del cálculo', 11, 'bold', 'left', 0, 4)
  txt(`Puntuación Grupo A: ${resultado.grupoA} (brazo=${resultado.puntuacionBrazo}, antebrazo=${resultado.puntuacionAntebrazo}, muñeca=${resultado.puntuacionMuneca}, giro=${resultado.puntuacionGiroMuneca})`, 9, 'normal', 'left', 0, 2)
  txt(`Puntuación Grupo B: ${resultado.grupoB} (cuello=${resultado.puntuacionCuello}, tronco=${resultado.puntuacionTronco}, piernas=${resultado.puntuacionPiernas})`, 9, 'normal', 'left', 0, 2)

  skip(3)

  // Tabla Grupo A: 3 sub-tablas por Antebrazo (1-3), cada una 6 filas (Brazo 1-6) x 8 columnas (Muñeca-Torsión)
  for (let antebrazoIdx = 1; antebrazoIdx <= 3; antebrazoIdx++) {
    const colHeaders = [];
    for (let m = 1; m <= 4; m++) {
      for (let t = 1; t <= 2; t++) {
        colHeaders.push(`M${m}-T${t}`);
      }
    }
    const rowHeaders = [1, 2, 3, 4, 5, 6].map(b => `Brazo ${b}`);
    const data = [1, 2, 3, 4, 5, 6].map(brazo => {
      const row = [];
      for (let m = 1; m <= 4; m++) {
        for (let t = 1; t <= 2; t++) {
          row.push(calcularPuntuacionGrupoA(brazo, antebrazoIdx, m, t));
        }
      }
      return row;
    });
    drawTable({
      title: `Tabla Grupo A — Antebrazo ${antebrazoIdx}`,
      colHeaders,
      rowHeaders,
      data,
      highlightRow: resultado.puntuacionAntebrazo === antebrazoIdx ? (resultado.puntuacionBrazo - 1) : -1,
      highlightCol: resultado.puntuacionAntebrazo === antebrazoIdx ? ((resultado.puntuacionMuneca - 1) * 2 + (resultado.puntuacionGiroMuneca - 1)) : -1,
    });
  }

  skip(3)

  // Tabla Grupo B: 6 filas (Cuello 1-6) x 12 columnas (Tronco-Piernas)
  const colHeadersB = [];
  for (let t = 1; t <= 6; t++) {
    for (let p = 1; p <= 2; p++) {
      colHeadersB.push(`Tr${t}-Pi${p}`);
    }
  }
  const rowHeadersB = [1, 2, 3, 4, 5, 6].map(c => `Cuello ${c}`);
  const dataB = [1, 2, 3, 4, 5, 6].map(cuello => {
    const row = [];
    for (let t = 1; t <= 6; t++) {
      for (let p = 1; p <= 2; p++) {
        row.push(calcularPuntuacionGrupoB(cuello, t, p));
      }
    }
    return row;
  });
  drawTable({
    title: 'Tabla Grupo B',
    colHeaders: colHeadersB,
    rowHeaders: rowHeadersB,
    data: dataB,
    highlightRow: resultado.puntuacionCuello - 1,
    highlightCol: (resultado.puntuacionTronco - 1) * 2 + (resultado.puntuacionPiernas - 1),
  });

  txt(`Puntuación C: ${resultado.puntuacionC}   Puntuación D: ${resultado.puntuacionD}`, 9, 'normal', 'left', 0, 2)
  txt(`Puntuación Final RULA: ${resultado.puntuacionFinal}`, 11, 'bold', 'left', 0, 3)
  txt(
    `Nivel ${resultado.nivelActuacion.nivel} — ${resultado.nivelActuacion.descripcion}`,
    10, 'bold', 'left', 0, 4
  )
  hline()

  // d. Disclaimer — mismo criterio que Ruido: el PDF documenta exactamente
  // lo que ve el usuario en pantalla, no una versión distinta.
  txt(
    'Tablas de doble entrada (Grupo A, Grupo B, Puntuación Final) extraídas de ' +
    'McAtamney, L. & Corlett, E.N. (1993). RULA: a survey method for the investigation of work-related upper limb disorders. Applied Ergonomics, 24(2), 91-99, y verificadas celda por celda contra la fuente. ' +
    'Este reporte no reemplaza el juicio de un profesional de ergonomía para el rediseño de la tarea.',
    8, 'italic', 'left', 0, 4
  )

  const blob = doc.output('blob')
  const filename = `RULA_${evaluacion.descripcion.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${evaluacion.id}-rula`, blob, filename, {
    category: 'procedimiento',
    title: `Reporte RULA - ${evaluacion.descripcion}`,
    description: 'Generado automáticamente desde Metodología RULA',
    checklist_item_id: 'GT-06',
    uploaded_by: uploadedBy,
  })
}
